import {
  ChevronDownIcon,
  ChevronUpIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  TrashIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { ApiResponse } from "@/api/rest/core/types";
import { DocumentRaw } from "@/api/rest/models/document";
import {
  EntityConfigurationEntityType,
  EntityConfigurationType,
  useUpdateConfiguration,
} from "@/api/rest/services/configurationService";
import { useBuildingDocuments } from "@/api/rest/services/documentService";
import {
  DefaultWarrantyCategory,
  StructuredWarrantyRules,
  WarrantyCategory,
  WarrantySubcontractor,
} from "@/api/rest/services/warrantyService";
import { ApiBanner } from "@/components/common/Banner";
import Button from "@/components/common/Button";
import Checkbox from "@/components/common/Checkbox";
import Field from "@/components/common/Field";
import Input from "@/components/common/Input";

interface DocumentPickerProps {
  documents: DocumentRaw[];
  selectedIds: number[];
  onChange: (ids: number[]) => void;
  isLoading?: boolean;
}

function getDocumentName(doc: DocumentRaw): string {
  return doc.filename || doc.originalFilename || `Document #${doc.id}`;
}

function DocumentPicker({ documents, selectedIds, onChange, isLoading }: DocumentPickerProps) {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const handleToggle = (docId: number) => {
    if (selectedIds.includes(docId)) {
      onChange(selectedIds.filter((id) => id !== docId));
    } else {
      onChange([...selectedIds, docId]);
    }
  };

  const selectedDocs = documents.filter((d) => selectedIds.includes(d.id));
  const unselectedDocs = documents.filter((d) => !selectedIds.includes(d.id));

  const filteredUnselectedDocs = useMemo(() => {
    if (!searchQuery.trim()) {
      return unselectedDocs;
    }
    const query = searchQuery.toLowerCase();
    return unselectedDocs.filter((doc) => getDocumentName(doc).toLowerCase().includes(query));
  }, [unselectedDocs, searchQuery]);

  if (isLoading) {
    return <div className="text-sm text-gray-500">{t("common.loading")}</div>;
  }

  if (documents.length === 0) {
    return <div className="text-sm text-gray-500">{t("structuredWarrantySettings.noDocuments")}</div>;
  }

  return (
    <div className="border rounded-md bg-white">
      {/* Selected documents */}
      {selectedDocs.length > 0 && (
        <div className="p-2 space-y-1">
          {selectedDocs.map((doc) => (
            <label key={doc.id} className="flex items-center gap-2 p-1 rounded hover:bg-gray-50 cursor-pointer">
              <input
                type="checkbox"
                checked
                onChange={() => handleToggle(doc.id)}
                className="rounded border-gray-300 text-orange-500 focus:ring-orange-500"
              />
              <span className="text-sm truncate flex-1" title={getDocumentName(doc)}>
                {getDocumentName(doc)}
              </span>
              <span className="text-xs text-gray-400 shrink-0">#{doc.id}</span>
            </label>
          ))}
        </div>
      )}

      {/* Expand/collapse toggle - using div instead of button to avoid global orange CSS */}
      {unselectedDocs.length > 0 && (
        <>
          <div
            role="button"
            tabIndex={0}
            onClick={() => setIsExpanded(!isExpanded)}
            onKeyDown={(e) => e.key === "Enter" && setIsExpanded(!isExpanded)}
            className={`w-full px-3 py-2 text-sm text-gray-600 hover:bg-gray-100 flex items-center justify-between cursor-pointer ${selectedDocs.length > 0 ? "border-t" : ""}`}
          >
            <span>
              {isExpanded
                ? t("structuredWarrantySettings.hideDocuments")
                : t("structuredWarrantySettings.showDocuments", {
                    count: unselectedDocs.length,
                  })}
            </span>
            {isExpanded ? <ChevronUpIcon className="h-4 w-4" /> : <ChevronDownIcon className="h-4 w-4" />}
          </div>

          {isExpanded && (
            <div className="border-t">
              {/* Search input */}
              <div className="p-2 border-b">
                <div className="relative">
                  <MagnifyingGlassIcon className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder={t("structuredWarrantySettings.searchDocuments")}
                    className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-orange-500 focus:border-orange-500"
                  />
                </div>
              </div>

              {/* Document list */}
              <div className="p-2 space-y-1 max-h-48 overflow-y-auto">
                {filteredUnselectedDocs.length === 0 ? (
                  <div className="text-sm text-gray-500 py-2 text-center">
                    {t("structuredWarrantySettings.noMatchingDocuments")}
                  </div>
                ) : (
                  filteredUnselectedDocs.map((doc) => (
                    <label key={doc.id} className="flex items-center gap-2 p-1 rounded hover:bg-gray-50 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={false}
                        onChange={() => handleToggle(doc.id)}
                        className="rounded border-gray-300 text-orange-500 focus:ring-orange-500"
                      />
                      <span className="text-sm truncate flex-1" title={getDocumentName(doc)}>
                        {getDocumentName(doc)}
                      </span>
                      <span className="text-xs text-gray-400 shrink-0">#{doc.id}</span>
                    </label>
                  ))
                )}
              </div>
            </div>
          )}
        </>
      )}

      {selectedDocs.length === 0 && !isExpanded && unselectedDocs.length > 0 && (
        <div className="p-2 text-sm text-gray-500">{t("structuredWarrantySettings.noDocsSelected")}</div>
      )}
    </div>
  );
}

interface StructuredWarrantySettingsProps {
  initialConfiguration: StructuredWarrantyRules;
  buildingId: number;
}

const emptyCategory: WarrantyCategory = {
  id: "",
  name: "",
  description: "",
  keywords: [],
  documentIds: [],
  customPrompt: null,
};

const emptySubcontractor: WarrantySubcontractor = {
  id: "",
  name: "",
  email: "",
  categories: [{ ...emptyCategory }],
};

export function StructuredWarrantySettings({ initialConfiguration, buildingId }: StructuredWarrantySettingsProps) {
  const { t } = useTranslation();
  const { mutate: updateConfiguration, isPending: isSaving } = useUpdateConfiguration();
  const { data: buildingDocuments = [], isLoading: isLoadingDocuments } = useBuildingDocuments(buildingId);

  const [isEnabled, setIsEnabled] = useState(initialConfiguration.enabled);
  const [subcontractors, setSubcontractors] = useState<WarrantySubcontractor[]>(initialConfiguration.subcontractors);
  const [defaultCategory, setDefaultCategory] = useState<DefaultWarrantyCategory | null>(
    initialConfiguration.defaultCategory ?? null,
  );

  const [saveResult, setSaveResult] = useState<ApiResponse<unknown> | null>(null);

  useEffect(() => {
    setIsEnabled(initialConfiguration.enabled);
    setSubcontractors(initialConfiguration.subcontractors);
    setDefaultCategory(initialConfiguration.defaultCategory ?? null);
  }, [initialConfiguration]);

  const handleAddSubcontractor = () => {
    const now = Date.now();
    setSubcontractors((prev) => [
      ...prev,
      {
        ...emptySubcontractor,
        id: `sub-${now}`,
        categories: [{ ...emptyCategory, id: `cat-${now}` }],
      },
    ]);
    setSaveResult(null);
  };

  const handleRemoveSubcontractor = (index: number) => {
    setSubcontractors((prev) => prev.filter((_, i) => i !== index));
    setSaveResult(null);
  };

  const handleSubcontractorChange = (index: number, field: keyof WarrantySubcontractor, value: string) => {
    const newSubcontractors = [...subcontractors];
    newSubcontractors[index] = { ...newSubcontractors[index], [field]: value };
    setSubcontractors(newSubcontractors);
    setSaveResult(null);
  };

  const handleAddCategory = (subIndex: number) => {
    const newSubcontractors = [...subcontractors];
    newSubcontractors[subIndex] = {
      ...newSubcontractors[subIndex],
      categories: [...newSubcontractors[subIndex].categories, { ...emptyCategory, id: `cat-${Date.now()}` }],
    };
    setSubcontractors(newSubcontractors);
    setSaveResult(null);
  };

  const handleRemoveCategory = (subIndex: number, catIndex: number) => {
    const newSubcontractors = [...subcontractors];
    newSubcontractors[subIndex] = {
      ...newSubcontractors[subIndex],
      categories: newSubcontractors[subIndex].categories.filter((_, i) => i !== catIndex),
    };
    setSubcontractors(newSubcontractors);
    setSaveResult(null);
  };

  const handleCategoryChange = (
    subIndex: number,
    catIndex: number,
    field: keyof WarrantyCategory,
    value: string | string[] | number[],
  ) => {
    const newSubcontractors = [...subcontractors];
    const newCategories = [...newSubcontractors[subIndex].categories];
    newCategories[catIndex] = { ...newCategories[catIndex], [field]: value };
    newSubcontractors[subIndex] = { ...newSubcontractors[subIndex], categories: newCategories };
    setSubcontractors(newSubcontractors);
    setSaveResult(null);
  };

  const handleDefaultCategoryChange = (field: keyof DefaultWarrantyCategory, value: string) => {
    setDefaultCategory((prev) => (prev ? { ...prev, [field]: value } : { email: "", documentIds: [], [field]: value }));
    setSaveResult(null);
  };

  const handleSave = (enabled?: boolean, options?: { showBanner: boolean }) => {
    if (options?.showBanner) {
      setSaveResult(null);
    }

    // Normalize optional string fields: empty or whitespace-only values should be null
    const normalizeOptionalString = (value: string | null | undefined): string | null => {
      if (!value || !value.trim()) {
        return null;
      }
      return value;
    };

    const normalizedSubcontractors = subcontractors.map((sub) => ({
      ...sub,
      categories: sub.categories.map((cat) => ({
        ...cat,
        customPrompt: normalizeOptionalString(cat.customPrompt),
      })),
    }));

    const normalizedDefaultCategory = defaultCategory
      ? {
          ...defaultCategory,
          name: normalizeOptionalString(defaultCategory.name),
          description: normalizeOptionalString(defaultCategory.description),
          customPrompt: normalizeOptionalString(defaultCategory.customPrompt),
        }
      : null;

    const configToSave: StructuredWarrantyRules = {
      enabled: enabled ?? isEnabled,
      subcontractors: normalizedSubcontractors,
      defaultCategory: normalizedDefaultCategory,
    };

    updateConfiguration(
      {
        entityType: EntityConfigurationEntityType.BUILDING,
        buildingId: buildingId,
        configurationType: EntityConfigurationType.STRUCTURED_WARRANTY_RULES,
        configurationValue: JSON.stringify(configToSave),
      },
      {
        onSuccess: () => {
          if (options?.showBanner) {
            setSaveResult({ success: true, result: {} });
          }
        },
        onError: (error) => {
          if (options?.showBanner) {
            setSaveResult({
              success: false,
              message: error.message,
              result: undefined,
            });
          }
        },
      },
    );
  };

  return (
    <div>
      <Checkbox
        label={t("structuredWarrantySettings.enable")}
        id="structured-warranty-enabled"
        checked={isEnabled}
        onChange={(e) => {
          const isChecked = e.target.checked;
          setIsEnabled(isChecked);
          if (isChecked && subcontractors.length === 0) {
            handleAddSubcontractor();
          }
          handleSave(isChecked, { showBanner: false });
        }}
      />

      {isEnabled && (
        <div className="mt-4 pl-2 space-y-6">
          {/* Subcontractors */}
          <div>
            <h4 className="font-semibold mb-2">{t("structuredWarrantySettings.subcontractors")}</h4>

            {subcontractors.map((sub, subIndex) => (
              <div key={`sub-${subIndex}`} className="relative mb-4 p-4 border rounded-md bg-slate-50">
                <button
                  onClick={() => handleRemoveSubcontractor(subIndex)}
                  className="absolute top-2 right-2 p-1 text-red-600 hover:text-red-800"
                  aria-label={t("structuredWarrantySettings.removeSubcontractor")}
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <Field id={`sub-name-${subIndex}`}>
                    <Field.Label>{t("structuredWarrantySettings.subcontractorName")}</Field.Label>
                    <Input
                      type="text"
                      value={sub.name}
                      onChange={(e) => handleSubcontractorChange(subIndex, "name", e.target.value)}
                      placeholder="e.g., HVAC Company Ltd"
                    />
                  </Field>

                  <Field id={`sub-email-${subIndex}`}>
                    <Field.Label>{t("structuredWarrantySettings.subcontractorEmail")}</Field.Label>
                    <Input
                      type="email"
                      value={sub.email}
                      onChange={(e) => handleSubcontractorChange(subIndex, "email", e.target.value)}
                      placeholder="e.g., warranty@hvac.com"
                    />
                  </Field>
                </div>

                {/* Categories for this subcontractor */}
                <div className="ml-4 border-l-2 border-gray-300 pl-4">
                  <h5 className="font-medium mb-2">{t("structuredWarrantySettings.categories")}</h5>

                  {sub.categories.map((cat, catIndex) => (
                    <div key={`cat-${subIndex}-${catIndex}`} className="relative mb-3 p-3 border rounded bg-white">
                      <button
                        onClick={() => handleRemoveCategory(subIndex, catIndex)}
                        className="absolute top-1 right-1 p-1 text-red-500 hover:text-red-700"
                        aria-label={t("structuredWarrantySettings.removeCategory")}
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>

                      <Field id={`cat-name-${subIndex}-${catIndex}`}>
                        <Field.Label>{t("structuredWarrantySettings.categoryName")}</Field.Label>
                        <Input
                          type="text"
                          value={cat.name}
                          onChange={(e) => handleCategoryChange(subIndex, catIndex, "name", e.target.value)}
                          placeholder="e.g., Heating System"
                        />
                      </Field>

                      <Field id={`cat-desc-${subIndex}-${catIndex}`} className="mt-2">
                        <Field.Label>{t("structuredWarrantySettings.categoryDescription")}</Field.Label>
                        <textarea
                          className="w-full border border-gray-300 rounded-md p-2 text-sm"
                          value={cat.description}
                          onChange={(e) => handleCategoryChange(subIndex, catIndex, "description", e.target.value)}
                          rows={2}
                          placeholder={t("structuredWarrantySettings.categoryDescriptionPlaceholder")}
                        />
                      </Field>

                      <Field id={`cat-docs-${subIndex}-${catIndex}`} className="mt-2">
                        <Field.Label>{t("structuredWarrantySettings.categoryDocs")}</Field.Label>
                        <DocumentPicker
                          documents={buildingDocuments}
                          selectedIds={cat.documentIds}
                          onChange={(ids) => handleCategoryChange(subIndex, catIndex, "documentIds", ids)}
                          isLoading={isLoadingDocuments}
                        />
                      </Field>

                      <Field id={`cat-prompt-${subIndex}-${catIndex}`} className="mt-2">
                        <Field.Label>{t("structuredWarrantySettings.customPrompt")}</Field.Label>
                        <textarea
                          className="w-full border border-gray-300 rounded-md p-2 text-sm"
                          value={cat.customPrompt ?? ""}
                          onChange={(e) => handleCategoryChange(subIndex, catIndex, "customPrompt", e.target.value)}
                          rows={3}
                          placeholder={t("structuredWarrantySettings.customPromptPlaceholder")}
                        />
                      </Field>
                    </div>
                  ))}

                  <Button variant="outline" size="sm" onClick={() => handleAddCategory(subIndex)} className="mt-2">
                    <PlusIcon className="h-4 w-4 mr-1" />
                    {t("structuredWarrantySettings.addCategory")}
                  </Button>
                </div>
              </div>
            ))}

            <Button variant="outline" onClick={handleAddSubcontractor} className="flex items-center">
              <PlusIcon className="h-5 w-5 mr-2" />
              {t("structuredWarrantySettings.addSubcontractor")}
            </Button>
          </div>

          {/* Default Category */}
          <div className="p-4 border rounded-md bg-gray-50">
            <h4 className="font-semibold mb-2">{t("structuredWarrantySettings.defaultCategory")}</h4>

            <Field id="default-name" className="mb-2">
              <Field.Label>{t("structuredWarrantySettings.defaultCategoryName")}</Field.Label>
              <Input
                type="text"
                value={defaultCategory?.name ?? ""}
                onChange={(e) => handleDefaultCategoryChange("name", e.target.value)}
                placeholder="e.g., General / Developer Issues"
              />
            </Field>

            <Field id="default-desc" className="mb-2">
              <Field.Label>{t("structuredWarrantySettings.defaultCategoryDescription")}</Field.Label>
              <textarea
                className="w-full border border-gray-300 rounded-md p-2 text-sm"
                value={defaultCategory?.description ?? ""}
                onChange={(e) => handleDefaultCategoryChange("description", e.target.value)}
                rows={2}
                placeholder={t("structuredWarrantySettings.categoryDescriptionPlaceholder")}
              />
            </Field>

            <Field id="default-email" className="mb-2">
              <Field.Label>{t("structuredWarrantySettings.defaultEmail")}</Field.Label>
              <Input
                type="email"
                value={defaultCategory?.email ?? ""}
                onChange={(e) => handleDefaultCategoryChange("email", e.target.value)}
                placeholder="e.g., developer@company.com"
              />
            </Field>

            <Field id="default-docs">
              <Field.Label>{t("structuredWarrantySettings.defaultDocs")}</Field.Label>
              <DocumentPicker
                documents={buildingDocuments}
                selectedIds={defaultCategory?.documentIds ?? []}
                onChange={(ids) => {
                  setDefaultCategory((prev) =>
                    prev ? { ...prev, documentIds: ids } : { email: "", documentIds: ids },
                  );
                  setSaveResult(null);
                }}
                isLoading={isLoadingDocuments}
              />
            </Field>

            <Field id="default-prompt" className="mt-2">
              <Field.Label>{t("structuredWarrantySettings.customPrompt")}</Field.Label>
              <textarea
                className="w-full border border-gray-300 rounded-md p-2 text-sm"
                value={defaultCategory?.customPrompt ?? ""}
                onChange={(e) => {
                  setDefaultCategory((prev) =>
                    prev
                      ? { ...prev, customPrompt: e.target.value }
                      : { email: "", documentIds: [], customPrompt: e.target.value },
                  );
                  setSaveResult(null);
                }}
                rows={3}
                placeholder={t("structuredWarrantySettings.customPromptPlaceholder")}
              />
            </Field>
          </div>

          {/* Save Button */}
          <div className="flex items-center gap-4 mt-4">
            <Button onClick={() => handleSave(undefined, { showBanner: true })} disabled={isSaving} loading={isSaving}>
              {isSaving ? t("settings.saving") : t("settings.save")}
            </Button>
            {saveResult && <ApiBanner apiResponse={saveResult} successMessage={t("settings.save")} />}
          </div>
        </div>
      )}
    </div>
  );
}

export default StructuredWarrantySettings;
